import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Button, Group, Loader, Alert, Text, Title, Badge,
  Table, ScrollArea, Paper, Select, Stack, Radio
} from '@mantine/core';
import { DatePickerInput, MonthPickerInput } from '@mantine/dates';
import { IconAlertCircle, IconRefresh, IconAdjustments, IconList } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import useAuthenticatedPost from '../../../hooks/useAuthenticatedPost';
import config from '../../../config';

function getPreviousQuarters(count = 8) {
  const today = new Date();
  let year = today.getFullYear();
  let quarter = Math.floor(today.getMonth() / 3) + 1;
  const quarters = [];
  for (let i = 0; i < count; i++) {
    quarters.push({ year, quarter });
    quarter--;
    if (quarter === 0) { quarter = 4; year--; }
  }
  return quarters;
}

function getQuarterDates(year, quarter) {
  const startMonth = (quarter - 1) * 3;
  return {
    from: new Date(year, startMonth, 1),
    to: new Date(year, startMonth + 3, 0),
  };
}

function toISO(date) {
  return date ? date.toISOString().split('T')[0] : null;
}

function fmtMoney(v, compact = false) {
  if (v == null) return '—';
  if (compact) {
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  }
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtPct(v) {
  if (v == null) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}

function pctColor(v) {
  if (v == null) return 'dimmed';
  if (v >= 0) return 'green';
  if (v >= -5) return 'yellow.7';
  if (v >= -10) return 'orange';
  return 'red';
}

function deltaColor(v) {
  if (v == null || Math.abs(v) < 0.05) return 'dimmed';
  return v > 0 ? 'green' : 'red';
}

export default function RepPricingDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { post } = useAuthenticatedPost();

  const paramMode    = searchParams.get('mode');
  const paramQuarter = searchParams.get('quarter');
  const paramMonth   = searchParams.get('month');
  const paramFrom    = searchParams.get('from') ?? '';
  const paramTo      = searchParams.get('to') ?? '';

  const [mode, setMode] = useState(paramMode ?? (paramFrom ? 'custom' : 'quarter'));
  const today = new Date();
  const currentQ = Math.floor(today.getMonth() / 3) + 1;
  const [selectedQuarter, setSelectedQuarter] = useState(
    paramQuarter ?? `${today.getFullYear()}-${currentQ}`
  );
  const [selectedMonth, setSelectedMonth] = useState(() => {
    if (paramMonth) {
      const [y, m] = paramMonth.split('-').map(Number);
      return new Date(y, m - 1, 1);
    }
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d;
  });
  const [customFrom, setCustomFrom] = useState(paramFrom ? new Date(paramFrom) : null);
  const [customTo, setCustomTo]     = useState(paramTo   ? new Date(paramTo)   : null);

  const [recalcState, setRecalcState] = useState({ status: 'idle', refreshedAt: null });

  const quarterOptions = useMemo(
    () => getPreviousQuarters(8).map(({ year, quarter }) => ({
      value: `${year}-${quarter}`,
      label: `Q${quarter} ${year}`,
    })),
    []
  );

  const { fromDate, toDate } = useMemo(() => {
    if (mode === 'quarter' && selectedQuarter) {
      const [y, q] = selectedQuarter.split('-').map(Number);
      const { from, to } = getQuarterDates(y, q);
      return { fromDate: toISO(from), toDate: toISO(to) };
    }
    if (mode === 'month' && selectedMonth) {
      const y = selectedMonth.getFullYear();
      const m = selectedMonth.getMonth();
      return { fromDate: toISO(new Date(y, m, 1)), toDate: toISO(new Date(y, m + 1, 0)) };
    }
    if (mode === 'custom') {
      return { fromDate: toISO(customFrom), toDate: toISO(customTo) };
    }
    return { fromDate: null, toDate: null };
  }, [mode, selectedQuarter, selectedMonth, customFrom, customTo]);

  const currentPeriodParam = mode === 'quarter' && selectedQuarter ? `mode=quarter&quarter=${selectedQuarter}`
    : mode === 'month' && selectedMonth ? `mode=month&month=${toISO(selectedMonth)?.slice(0, 7)}`
    : fromDate && toDate ? `mode=custom&from=${fromDate}&to=${toDate}`
    : '';

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('mode', mode);
    if (mode === 'quarter' && selectedQuarter) params.set('quarter', selectedQuarter);
    if (mode === 'month' && selectedMonth) {
      const y = selectedMonth.getFullYear();
      const mo = String(selectedMonth.getMonth() + 1).padStart(2, '0');
      params.set('month', `${y}-${mo}`);
    }
    if (mode === 'custom') {
      if (customFrom) params.set('from', toISO(customFrom));
      if (customTo)   params.set('to',   toISO(customTo));
    }
    setSearchParams(params, { replace: true });
  }, [mode, selectedQuarter, selectedMonth, customFrom, customTo, setSearchParams]);

  const apiUrl = fromDate && toDate
    ? `${config.apiBaseUrl}/SalesManager/PricingDashboard?from=${fromDate}&to=${toDate}`
    : null;

  const { data: rows, isLoading, error } = useAuthenticatedData(apiUrl);

  const summary = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const totalRevenue      = rows.reduce((s, r) => s + (r.revenue ?? 0), 0);
    const avgBase           = rows.reduce((s, r) => s + (r.baseSvspPct ?? 0), 0) / rows.length;
    const avgAdj            = rows.reduce((s, r) => s + (r.adjSvspPct  ?? 0), 0) / rows.length;
    const totalOverrides    = rows.reduce((s, r) => s + (r.adjustmentCount ?? 0), 0);
    return { totalRevenue, avgBase, avgAdj, totalOverrides };
  }, [rows]);

  const handleRecalc = async () => {
    setRecalcState({ status: 'running', refreshedAt: null });
    try {
      const result = await post(`${config.apiBaseUrl}/sales/invoice-overrides/recalculate-scorecards`, {});
      setRecalcState({ status: 'done', refreshedAt: result?.refreshedAt });
    } catch {
      setRecalcState({ status: 'error', refreshedAt: null });
    }
  };

  return (
    <Box p="xl">
      {/* Header */}
      <Group justify="space-between" mb="md" align="flex-end">
        <Title order={2}>Pricing Adjustments</Title>
        <Group gap="lg" align="flex-end">
          {fromDate && toDate && (
            <Text size="sm" c="dimmed" style={{ textAlign: 'right' }}>
              {new Date(fromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              {' — '}
              {new Date(toDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Text>
          )}
          <Button
            variant="light"
            size="sm"
            leftSection={<IconList size={16} />}
            onClick={() => navigate(`/sales/manager/invoices?${currentPeriodParam}`)}
          >
            All Invoices
          </Button>
          <Button
          variant="subtle"
          size="sm"
          leftSection={recalcState.status === 'running' ? <Loader size="xs" /> : <IconRefresh size={16} />}
          disabled={recalcState.status === 'running'}
          onClick={handleRecalc}
        >
          {recalcState.status === 'running' ? 'Running…'
            : recalcState.status === 'done'    ? `Recalculated`
            : recalcState.status === 'error'   ? 'Failed — retry'
            : 'Recalculate Scorecards'}
          </Button>
        </Group>
      </Group>

      {/* Period picker */}
      <Paper withBorder p="sm" mb="md">
        <Group gap="xl" align="flex-end" wrap="wrap">
          <Radio.Group value={mode} onChange={setMode} label="Period">
            <Group gap="sm" mt={4}>
              <Radio value="quarter" label="Quarter" size="sm" />
              <Radio value="month"   label="Month"   size="sm" />
              <Radio value="custom"  label="Custom"  size="sm" />
            </Group>
          </Radio.Group>
          {mode === 'quarter' && (
            <Select
              label="Quarter"
              value={selectedQuarter}
              onChange={v => { if (v) setSelectedQuarter(v); }}
              data={quarterOptions}
              style={{ width: 160 }}
              size="sm"
            />
          )}
          {mode === 'month' && (
            <MonthPickerInput
              label="Month"
              value={selectedMonth}
              onChange={setSelectedMonth}
              style={{ width: 180 }}
              size="sm"
            />
          )}
          {mode === 'custom' && (
            <>
              <DatePickerInput label="From" value={customFrom} onChange={setCustomFrom} style={{ width: 160 }} size="sm" clearable />
              <DatePickerInput label="To"   value={customTo}   onChange={setCustomTo}   style={{ width: 160 }} size="sm" clearable />
            </>
          )}
        </Group>
      </Paper>

      {summary && (
        <Group justify="flex-end" mb={4}>
          <Text size="xs" c="dimmed">Class A &amp; B only</Text>
        </Group>
      )}

      {/* Summary stat cards */}
      {summary && (
        <Group mb="md" grow>
          <Paper withBorder p="md" ta="center">
            <Text size="xs" c="dimmed" mb={4}>Total Revenue</Text>
            <Text size="xl" fw={700}>{fmtMoney(summary.totalRevenue, true)}</Text>
          </Paper>
          <Paper withBorder p="md" ta="center">
            <Text size="xs" c="dimmed" mb={4}>Avg Base SvSP%</Text>
            <Text size="xl" fw={700} c={pctColor(summary.avgBase)}>{fmtPct(summary.avgBase)}</Text>
          </Paper>
          <Paper withBorder p="md" ta="center">
            <Text size="xs" c="dimmed" mb={4}>Avg Adj SvSP%</Text>
            <Text size="xl" fw={700} c={pctColor(summary.avgAdj)}>{fmtPct(summary.avgAdj)}</Text>
          </Paper>
          <Paper
            withBorder p="md" ta="center"
            style={summary.totalOverrides > 0 ? { cursor: 'pointer' } : undefined}
            onClick={summary.totalOverrides > 0 ? () => navigate(`/sales/manager/overrides?from=${fromDate}&to=${toDate}&isValid=true`) : undefined}
          >
            <Text size="xs" c="dimmed" mb={4}>Override Lines</Text>
            <Text size="xl" fw={700} c={summary.totalOverrides > 0 ? 'teal' : 'dimmed'}>{summary.totalOverrides}</Text>
          </Paper>
        </Group>
      )}

      {isLoading && <Group justify="center" py="xl"><Loader /></Group>}
      {error && (
        <Alert icon={<IconAlertCircle />} color="red" mb="md">
          Failed to load dashboard data.
        </Alert>
      )}

      {!isLoading && !error && Array.isArray(rows) && rows.length > 0 && (
        <Paper withBorder>
          <ScrollArea>
            <Table highlightOnHover verticalSpacing={6} horizontalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Salesperson</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Priced</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Unpriced</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Base SvSP%</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Adj SvSP%</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Adj Impact</Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Overrides</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map(row => {
                  const hasAdj = (row.adjustmentCount ?? 0) > 0;
                  const delta = hasAdj ? (row.adjSvspPct ?? 0) - (row.baseSvspPct ?? 0) : null;
                  return (
                    <Table.Tr key={row.ccode}>
                      <Table.Td fw={500}>{row.name ?? row.ccode}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm">{fmtMoney(row.revenue, true)}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm">{row.invoiceLines ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        {row.unpricedLines > 0
                          ? <Text size="sm" c="orange" fw={500}
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/sales/manager/invoices?ccode=${row.ccode}&${currentPeriodParam}&tab=unpriced`)}
                            >{row.unpricedLines}</Text>
                          : <Text size="sm" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" c={pctColor(row.baseSvspPct)} fw={500}>{fmtPct(row.baseSvspPct)}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        {hasAdj
                          ? <Text size="sm" c={pctColor(row.adjSvspPct)} fw={600}>{fmtPct(row.adjSvspPct)}</Text>
                          : <Text size="sm" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" c={delta == null ? 'dimmed' : deltaColor(delta)}>
                          {delta == null || Math.abs(delta) < 0.05 ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'center' }}>
                        {row.adjustmentCount > 0
                          ? <Badge
                              size="sm" color="teal" variant="light"
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/sales/manager/overrides?ccode=${row.ccode}&from=${fromDate}&to=${toDate}&isValid=true`)}
                            >{row.adjustmentCount}</Badge>
                          : <Text size="sm" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconAdjustments size={13} />}
                          onClick={() => navigate(`/sales/manager/invoices?ccode=${row.ccode}&${currentPeriodParam}`)}
                        >
                          Adjust
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}

      {!isLoading && !error && Array.isArray(rows) && rows.length === 0 && (
        <Text c="dimmed" ta="center" py="xl">No pricing data for this period.</Text>
      )}
    </Box>
  );
}
